<template>
    <div id="slider" ref="slider">
        <div class="slides" ref="slides">
            <div class="slides-inner">
                <div
                    class="slide"
                    :class="{
                        prev: activeIndex > index,
                        next: activeIndex < index,
                        'show-meta': activeIndex == index,
                    }"
                    v-for="(slide, index) in cases"
                    :key="index"
                >
                    <div class="slide-inner">
                        <aside class="slider__pagination-holder">
                            <div
                                class="slider__pagination slider__pagination--current"
                            >
                                0{{ activeIndex + 1 }}.
                            </div>
                            <div class="slider__pagination-divider"></div>
                            <div
                                class="slider__pagination slider__pagination--total"
                            >
                                0{{ cases.length }}.
                            </div>
                        </aside>
                        <h1
                            class="slide-title"
                            style="visibility: hidden"
                            v-html="slide.title"
                        ></h1>
                        <a
                            data-cursor-enlarge
                            class="slide-meta"
                            @click.prevent.stop="onReadMore(slide.case, $event)"
                            @mousedown.prevent.stop=""
                            @mouseup.prevent.stop=""
                            :href="slide.case"
                        >
                            <div class="slide-meta__text">
                                {{ slide.subtitle }}
                            </div>
                            <svg class="slide-meta__cta" width="45" height="45" viewBox="0 0 45 45" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="22.5" cy="22.5" r="22.5" fill="white"/>
                                <path fill-rule="evenodd" clip-rule="evenodd" d="M29.352 22.3674L22.4305 15.337L23.2545 14.5L31 22.3674L23.2545 30.2348L22.4305 29.3979L29.352 22.3674Z" fill="#1F1F21"/>
                                <path d="M13 21.8344H30.1298V23.0181H13V21.8344Z" fill="#1F1F21"/>
                            </svg>
                        </a>
                    </div>
                </div>
            </div>
        </div>

        <transition name="fade" appear>
        <div class="blob-loader" v-show="loading">
            <div class="small-blob"><span></span></div>
            <div><span></span></div>
        </div>
        </transition>
    </div>
</template>

<script>
import Preload from 'preload-it';
import { gsap } from 'gsap/all';
import SplitText from './../gsap-bonus/SplitText';
import debounce from 'lodash/debounce';

import { Showcase } from './../classes/Showcase';
import Slides from './../classes/Slides';

export default {
    name: 'HomeSlider',
    data() {
        return {
            loading: false,
            slides: null,
            showcase: null,
            activeIndex: 0,
            toggle: false,
            slider: null,
        };
    },

    props: {
        cases: {
            type: Array,
            default: [],
        },
    },

    mounted() {
        if (!window.preloadManifest) {
            console.log('No preload');
            this.init();
            return;
        }

        this.preload();
    },

    methods: {
        preload() {
            this.loading = true;
            const preloader = this.$el.querySelector('.blob-loader');
            const smallBlobSpan = preloader.querySelector('.small-blob span');

            preloader.classList.add('preload-started');

            const preload = Preload();
            preload.fetch(window.preloadManifest.map((object) => object.src));

            preload.oncomplete = (items) => {
                smallBlobSpan.addEventListener("animationiteration", this.finishPreload);
            };

            preload.onprogress = (event) => {
                // console.log(event.progress + '%');
            };

            preload.onfetched = (item) => {
                // console.log(item);
            };

            preload.onerror = (item) => {
                console.log(item);
            };
        },

        init() {
            // Lock scroll for side effects.
            this.$root.lockScroll();

            // Prepare the elements for animation.
            gsap.set('.slide.show-meta .slide-meta', {
                y: 10,
                autoAlpha: 0,
                transition: 'none',
            });
            // gsap.set('canvas', { autoAlpha: 0 });
            gsap.set('.slider__pagination', { autoAlpha: 0 });
            gsap.set(['.footnote', '.footer-links'], { autoAlpha: 0, y: 5 });
            gsap.set('.slider__pagination-divider', {
                scaleX: 0,
                transformOrigin: 'center center ',
            });

            // Prepare headings for animation by using SplitText.
            this.headings = [...document.querySelectorAll('h1')];
            this.headings.forEach((object, index) => {
                new SplitText(object, {
                    type: 'lines,words',
                    wordsClass: 'word word-++',
                    linesClass: 'oh',
                });
            });

            // Slider setup.
            this.setupSlides();
            this.addListeners();

            // Animate!
            this.animateIn();
        },

        finishPreload() {
            gsap.delayedCall(0.42, () => {
                this.loading = false;
                this.init();
            });
        },

        addListeners() {
            window.addEventListener('keydown', this.onKeyDown);
            window.addEventListener(
                'resize',
                this.onResize,
                window.passiveIfSupported
            );
            window.addEventListener(
                'mousemove',
                this.onMouseMove,
                window.passiveIfSupported
            );
            EventBus.$once('page-transition-leave', this.leave);
            EventBus.$once('slider:on-read-more', this.readMore);
            EventBus.$once('slider:transition-to', this.goToUrl);
            EventBus.$once('slider:destroy', this.onDestroy);
        },

        animateIn() {
            const timeline = gsap.timeline({ delay: 0.5 });

            const animateHeaders = () => {
                gsap.set(this.headings, { visibility: 'visible' });
                this.headings.forEach((object, index) => {
                    window.requestAnimationFrame(() => {
                        object.classList.add('--reveal');
                    });
                });
            };

            timeline.call(() => this.showcase.onGrabEnd(), ['this'], 0);
            timeline.call(() => animateHeaders(), ['this'], 0.5);
            timeline.to(
                'canvas',
                { duration: 0.2, autoAlpha: 1, ease: 'power4.easeOut' },
                0
            );
            timeline.to(
                ['.footnote', '.footer-links'],
                { duration: 0.3, autoAlpha: 1, y: 0, ease: 'power4.easeOut' },
                0.5
            );
            timeline.to(
                '.slide.show-meta .slide-meta',
                {
                    duration: 0.6,
                    y: 0,
                    autoAlpha: 1,
                    ease: 'power4.easeOut',
                    onComplete: () => {
                        gsap.set('.slide.show-meta .slide-meta', {
                            transition: '',
                        });
                    },
                },
                1
            );

            timeline.to('.slider__pagination', {
                duration: 0.3,
                autoAlpha: 1,
                ease: 'power4.easeOut',
                delay: 1,
            });
            timeline.to('.slider__pagination-divider', {
                duration: 0.3,
                scaleX: 1,
                transformOrigin: 'center center ',
                ease: 'power4.easeOut',
                delay: 1,
            });
        },

        setActiveIndex(index) {
            this.activeIndex = index;
        },

        onWheel(event) {
            this.slider.handleWheelInertia(event);
        },

        onMouseMove(event) {
            if (!this.showcase) {
                return;
            }

            this.showcase.onMouseMove(event);
        },

        onKeyDown(event) {
            switch (event.keyCode) {
                case 37:
                    this.slider.navigate(-1);
                    break;
                case 39:
                    this.slider.navigate(1);
                    break;
                case 13:
                    this.onReadMore();
                    break;
            }
        },

        onResize() {
            this.showcase.onResize();
        },

        onReadMore() {
            console.log('read more click');
            // Unlisten window.
            window.removeEventListener('keydown', this.onKeyDown);
            window.removeEventListener(
                'resize',
                this.onResize,
                window.passiveIfSupported
            );
            window.removeEventListener(
                'mousemove',
                this.onMouseMove,
                window.passiveIfSupported
            );

            document.documentElement.classList.remove('scroll-lock');

            // Set transition and go.
            this.$root.router.transition = 'slider-to-case';
            const object = this.cases[this.activeIndex];
            this.showcase.onReadMore(object.case);
            this.$root.cursor.leave();
        },

        goToUrl(url) {
            // Unlisten window.
            window.removeEventListener('keydown', this.onKeyDown);
            window.removeEventListener(
                'resize',
                this.onResize,
                window.passiveIfSupported
            );
            window.removeEventListener(
                'mousemove',
                this.onMouseMove,
                window.passiveIfSupported
            );

            document.documentElement.classList.remove('scroll-lock');

            // Set transition and go.
            this.$root.router.transition = 'slider-to-case';
            this.showcase.onReadMore(url);
            this.$root.cursor.leave();
        },

        onDestroy() {
            // Unlisten window.
            window.removeEventListener('keydown', this.onKeyDown);
            window.removeEventListener(
                'resize',
                this.onResize,
                window.passiveIfSupported
            );
            window.removeEventListener(
                'mousemove',
                this.onMouseMove,
                window.passiveIfSupported
            );

            document.documentElement.classList.remove('scroll-lock');
            this.showcase.destroy();
            this.$root.cursor.leave();
        },

        setupSlides() {
            const bounce = (value) => {
                this.showcase.onActiveIndexChange(value);
            };

            var debounceCalculate = debounce(bounce, 0, {
                leading: false,
                trailing: true,
            });

            this.slider = new Slides(this);
            this.slider.onActiveProjectIndexChange = (value) => {
                this.showcase.onActiveIndexChange(value);
            };
            this.showcase = new Showcase(this.cases, {
                onZoomOutStart: ({ activeIndex }) => {
                    this.$root.cursor.enter();
                },
                onZoomOutFinish: ({ activeIndex }) => {},
                onFullscreenStart: ({ activeIndex }) => {
                    this.$root.cursor.leave();
                },
                onFullscreenFinish: ({ activeIndex }) => {},
            });

            this.showcase.mount(this.$refs.slider);
            this.showcase.render();
        },

        leave() {
            EventBus.$emit('slider:destroy');
        },
    },

    beforeDestroy() {
        window.removeEventListener('keydown', this.onKeyDown);
        window.removeEventListener(
            'resize',
            this.onResize,
            window.passiveIfSupported
        );

        window.removeEventListener(
            'mousemove',
            this.onMouseMove,
            window.passiveIfSupported
        );

        EventBus.$off('slider:on-read-more', this.readMore);
        EventBus.$off('slider:transition-to', this.goToUrl);
        EventBus.$off('slider:destroy', this.onDestroy);
        EventBus.$off('page-transtion-leave', this.leave);
    },
};
</script>
